import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function FundraiserManagement() {
  const { token } = useAuth();
  const [fundraisers, setFundraisers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingFundraiser, setEditingFundraiser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    goal: 1400,
    bio: '',
    fullBio: '',
    type: 'individual',
    photo: ''
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';

  const fetchFundraisers = async () => {
    try {
      const response = await fetch(`${apiUrl}/fundraisers`);
      if (response.ok) {
        const data = await response.json();
        setFundraisers(data);
      }
    } catch (err) {
      console.error('Error fetching fundraisers:', err);
    }
  };

  useEffect(() => {
    fetchFundraisers();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      goal: 1400,
      bio: '',
      fullBio: '',
      type: 'individual',
      photo: ''
    });
    setEditingFundraiser(null);
    setShowForm(false);
  };

  const handleEdit = (fundraiser) => {
    setFormData({
      name: fundraiser.name,
      goal: fundraiser.goal,
      bio: fundraiser.bio,
      fullBio: fundraiser.fullBio || fundraiser.bio,
      type: fundraiser.type || 'individual',
      photo: fundraiser.photo || ''
    });
    setEditingFundraiser(fundraiser);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingFundraiser
        ? `${apiUrl}/fundraisers/${editingFundraiser.id}`
        : `${apiUrl}/fundraisers`;

      const method = editingFundraiser ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(editingFundraiser ? 'Fundraiser updated successfully!' : 'Fundraiser created successfully!');
        resetForm();
        fetchFundraisers();
      } else {
        setError(data.error || 'Failed to save fundraiser');
      }
    } catch (err) {
      console.error('Error saving fundraiser:', err);
      setError('Failed to save fundraiser');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiUrl}/fundraisers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`"${name}" deleted successfully!`);
        fetchFundraisers();
      } else {
        setError(data.error || 'Failed to delete fundraiser');
      }
    } catch (err) {
      console.error('Error deleting fundraiser:', err);
      setError('Failed to delete fundraiser');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold">Fundraiser Management</h2>
        <button
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ Add Fundraiser'}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold mb-4">
            {editingFundraiser ? 'Edit Fundraiser' : 'Create New Fundraiser'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                maxLength={100}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="individual">Individual</option>
                  <option value="project">Team Project</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Goal ($) *</label>
                <input
                  type="number"
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  min={100}
                  max={100000}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Photo URL</label>
              <input
                type="url"
                value={formData.photo}
                onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/photo.jpg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Short Bio * (10-500 characters)</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                required
                minLength={10}
                maxLength={500}
              />
              <p className="text-sm text-gray-600 mt-1">{formData.bio.length}/500</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Full Bio (max 2000 characters)</label>
              <textarea
                value={formData.fullBio}
                onChange={(e) => setFormData({ ...formData, fullBio: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={5}
                maxLength={2000}
              />
              <p className="text-sm text-gray-600 mt-1">{formData.fullBio.length}/2000</p>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
            >
              {editingFundraiser ? 'Update Fundraiser' : 'Create Fundraiser'}
            </button>
          </form>
        </div>
      )}

      {/* Fundraisers List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">All Fundraisers ({fundraisers.length})</h3>
        <div className="space-y-4">
          {fundraisers.map((fundraiser) => (
            <div
              key={fundraiser.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center space-x-4">
                <img
                  src={fundraiser.photo}
                  alt={fundraiser.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold">{fundraiser.name}</p>
                  <p className="text-sm text-gray-600">
                    Goal: ${fundraiser.goal.toLocaleString()} | Raised: ${(fundraiser.raised || 0).toLocaleString()}
                  </p>
                  <span className={`inline-block px-2 py-1 text-xs rounded ${
                    fundraiser.type === 'project'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {fundraiser.type === 'project' ? 'Team Project' : 'Individual'}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(fundraiser)}
                  className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(fundraiser.id, fundraiser.name)}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default FundraiserManagement;
